/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Suspense, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import messages from './messages';

// Lazy-loaded so the modal bundle is only fetched on interaction, not on initial paint.
const PreferenceModal = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_browser-storage-controls-preference-modal" */ '@atlassian/browser-storage-controls/consent-banner/preference-modal'
	).then((m) => ({
		default: m.PreferenceModal,
	})),
);

type CookiePreferencesButtonProps = {
	appearance?: 'default' | 'subtle';
	isInClassicHelpCenter?: boolean;
	spacing?: 'default' | 'compact';
};

const styles = cssMap({
	classicHelpCenter: {
		display: 'inline-flex',
		alignItems: 'center',
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
});

export const CookiePreferencesButton = ({
	appearance = 'subtle',
	spacing = 'compact',
	isInClassicHelpCenter = false,
}: CookiePreferencesButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	const handleOpen = useCallback(() => setIsOpen(true), []);
	const handleClose = useCallback(() => setIsOpen(false), []);
	const label = formatMessage(messages.cookiePreferences);

	return (
		<React.Fragment>
			{isInClassicHelpCenter ? (
				<Pressable xcss={styles.classicHelpCenter} onClick={handleOpen}>
					{label}
				</Pressable>
			) : (
				<Button appearance={appearance} spacing={spacing} onClick={handleOpen}>
					{label}
				</Button>
			)}
			{isOpen && (
				<Suspense fallback={null}>
					<PreferenceModal onClose={handleClose} onSubmit={handleClose} />
				</Suspense>
			)}
		</React.Fragment>
	);
};
